import type { ISbStoryData } from '@storyblok/js';
import type { StoryblokMultilink } from '~storyblok-types/storyblok';
import type { CountryLanguages } from '~/types/countryLanguages';
import log from '~/utils/log';
import { getStoryCountry, resolveStoryPath } from '~/utils/resolveStoryPath';

function isTranslatedSlug(entry: unknown): entry is { lang: string; path: string; published?: boolean | null } {
    return (
        typeof entry === 'object' &&
        entry !== null &&
        'lang' in entry &&
        typeof entry.lang === 'string' &&
        'path' in entry &&
        typeof entry.path === 'string'
    );
}

function isResolvableStory(story: unknown): boolean {
    if (!story || typeof story !== 'object') {
        return false;
    }

    const record = story as Record<string, unknown>;

    if (
        (typeof record.default_full_slug !== 'string' || !record.default_full_slug) &&
        (typeof record.full_slug !== 'string' || !record.full_slug)
    ) {
        return false;
    }

    return !Array.isArray(record.translated_slugs) || record.translated_slugs.every(isTranslatedSlug);
}

/**
 * This function checks if any source is a resolvable storyblok link
 * and can be resolved with {@link resolveStoryblokLink}.
 *
 * @param source - The source to check if it is a resolvable storyblok link.
 * @returns
 */
export function isResolvableStoryblokLink(source: unknown): source is StoryblokMultilink {
    if (!source || typeof source !== 'object') {
        return false;
    }

    const link = source as Record<string, unknown>;

    if (link.linktype === 'url') {
        return typeof link.url === 'string' && link.url.length > 0;
    }

    if (link.linktype !== 'story') {
        return false;
    }

    return isResolvableStory(link.story);
}

const hasStoryblokMultilinkStory = (
    link: StoryblokMultilink
): link is StoryblokMultilink & { story: StoryblokMultilinkStory } => {
    return typeof link === 'object' && link.linktype === 'story';
};

export const buildUrlFromStory = (
    story: ISbStoryData<unknown> | StoryblokMultilinkStory,
    countryLanguages: CountryLanguages,
    lang: string
) => {
    const storySlug = story.default_full_slug || story.full_slug;

    if (!storySlug) {
        throw new Error(`Story ${story.id} does not have a default_full_slug or full_slug`);
    }

    const country = getStoryCountry(story, countryLanguages);
    const countryConfig = countryLanguages[country];
    if (!countryConfig) {
        throw new Error(`Country not found in countryLanguages: ${country}`);
    }

    // Detect the language from the current language, falling back to the default language of the referenced country if the current language is not available for that country
    const availableLanguages = countryConfig.availableLanguages;
    const language = availableLanguages.includes(lang) ? lang : countryConfig.defaultLanguage;

    const localizedPath = resolveStoryPath(story, language, countryLanguages);
    if (localizedPath) return localizedPath;

    const defaultLanguage = countryConfig.defaultLanguage;
    const defaultPath = resolveStoryPath(story, defaultLanguage, countryLanguages);
    if (!defaultPath) {
        throw new Error(`Could not resolve story ${story.id} in language ${language} or ${defaultLanguage}`);
    }

    log.debug(`Could not resolve story ${story.id} in language ${language}, using ${defaultLanguage}`);
    return defaultPath;
};

/**
 * This function should resolve a Storyblok link into an URL which works with our routing:
 * - When referencing an story, it should return an URL with the correct country and language
 *   prefixes and translated slug of the story.
 * - The country should be defined by the referenced story, not the current country. This
 *   allows linking to stories in other countries. The country is the first segment of the
 *   slug.
 * - The language should be defined by the current language if possible. If the referenced
 *   country doesn't have this language available, it should fall back to the default language
 *   of the referenced country.
 * - The slug must use the `translated_slug` from the referenced story.
 * - When referencing an external URL, it should return the URL as is.
 */
export const resolveStoryblokLink = (link: StoryblokMultilink, countryLanguages: CountryLanguages, lang: string) => {
    if (hasStoryblokMultilinkStory(link)) {
        if (!link.story) {
            throw new Error(`Link with id ${link.id} does not have a story`);
        }

        return buildUrlFromStory(link.story, countryLanguages, lang);
    }

    if (link.linktype === 'url') {
        return link.url;
    }

    throw new Error(`Unsupported link type: ${link.linktype}`);
};
